// Papéis nos dados compartilhados do grupo (0.25): o que cada pessoa vê e altera.
// segs: partes extras que a pessoa vê além da base ("fin" = valores, "clin" = dados clínicos: o prontuário).
// ver / editar: coleções (["*"] = todas). O servidor aplica as mesmas regras (migracao-0.25.sql).
import type { ModId } from '../store/types';
import type { Campo, Colecao } from './tipos';
import { N_ } from '../lib/i18n';

export type Seg = 'base' | 'fin' | 'clin';
export type Papel = { nome: string; desc: string; segs: Seg[]; ver: string[]; editar: string[] };
export type Papeis = Record<string, Papel>;

const EDITOR: Papel = { nome: N_('Edita tudo'), desc: N_('Vê e altera tudo, inclusive os valores.'), segs: ['fin', 'clin'], ver: ['*'], editar: ['*'] };
const LEITOR: Papel = { nome: N_('Só acompanha'), desc: N_('Vê tudo, inclusive os valores, sem alterar.'), segs: ['fin', 'clin'], ver: ['*'], editar: [] };
// 0.29: o padrão dos grupos de família e amigos: participa de tudo (inclui, edita, põe fotos), só não gerencia as pessoas
const PARTICIPA: Papel = { nome: N_('Participa'), desc: N_('Vê tudo, inclui, edita, comenta e põe fotos. Só não coloca nem tira pessoas do grupo.'), segs: ['fin'], ver: ['*'], editar: ['*'] };
const LEITOR_SV: Papel = { nome: N_('Só acompanha, sem valores'), desc: N_('Vê o andamento, sem os valores e sem alterar.'), segs: [], ver: ['*'], editar: [] };

const ESPECIFICOS: Partial<Record<ModId, Papeis>> = {
  obras: {
    dono_obra: { nome: N_('Dono da obra'), desc: N_('Acompanha tudo, inclusive os gastos, e registra no diário.'), segs: ['fin'], ver: ['*'], editar: ['diario'] },
    engenheiro: { nome: N_('Engenheiro ou arquiteto'), desc: N_('Edita tudo: etapas, orçamento, diário e entregas.'), segs: ['fin'], ver: ['*'], editar: ['*'] },
    mestre: { nome: N_('Mestre de obras'), desc: N_('Registra o diário, as etapas e as entregas, sem ver os valores.'), segs: [], ver: ['*'], editar: ['diario', 'etapa', 'entrega'] },
    profissional: { nome: N_('Profissional'), desc: N_('Vê as etapas e registra o diário.'), segs: [], ver: ['obra', 'etapa', 'diario'], editar: ['diario'] },
  },
  consultorio: {
    // 0.65 (B14): "Para o paciente" (parapaciente) é o que pode sair do consultório; o prontuário fica em "clin"
    assistente: { nome: N_('Profissional assistente'), desc: N_('Prontuário, evolução e prescrições, sem o financeiro.'), segs: ['clin'], ver: ['*'], editar: ['paciente', 'atendimento', 'parapaciente', 'modelo'] },
    // a recepção vê (e pode reenviar pelo menu Enviar) o que é para o paciente, mas o conteúdo é do profissional
    secretaria: { nome: N_('Secretária ou recepção'), desc: N_('Agenda, cadastro dos pacientes e pagamentos, sem o prontuário.'), segs: ['fin'], ver: ['paciente', 'atendimento', 'parapaciente'], editar: ['paciente', 'atendimento'] },
  },
  hospedagem: {
    coanfitriao: { nome: N_('Co-anfitrião'), desc: N_('Cuida das reservas, da limpeza e das despesas.'), segs: ['fin'], ver: ['*'], editar: ['*'] },
    limpeza: { nome: N_('Equipe de limpeza'), desc: N_('Vê os check-ins e check-outs e marca a limpeza feita, sem valores.'), segs: [], ver: ['espaco', 'reserva', 'limpeza'], editar: ['limpeza'] },
    // 0.65 (B13): o dono do imóvel acompanha o resultado. Vê tudo com valores (as limpezas entram no lucro do mês,
    // por isso "*"), mas não altera nada: nem os dados dos hóspedes, nem as observações de quem administra.
    proprietario: { nome: N_('Proprietário / Locador'), desc: N_('Acompanha o imóvel, as reservas, a ocupação, as despesas e o lucro, sem alterar nada.'), segs: ['fin'], ver: ['*'], editar: [] },
  },
  viagens: {
    viajante: { nome: N_('Vai na viagem'), desc: N_('Vê tudo e lança gastos, lugares, roteiro e mala.'), segs: ['fin'], ver: ['*'], editar: ['roteiro', 'mala', 'lugar', 'gasto', 'docviagem'] },
  },
  professor: {
    monitor: { nome: N_('Monitor ou co-professor'), desc: N_('Chamada, notas, planos de aula e materiais.'), segs: [], ver: ['*'], editar: ['chamada', 'avaliacao', 'aula', 'material'] },
    aluno: { nome: N_('Aluno'), desc: N_('Vê as aulas e os materiais liberados.'), segs: [], ver: ['turma', 'aula', 'material'], editar: [] },
  },
  eventos: {
    ajudante: { nome: N_('Ajuda na organização'), desc: N_('Convidados e cronograma do dia, sem os valores.'), segs: [], ver: ['*'], editar: ['convidado', 'cronograma'] },
  },
  portal: {
    cliente: { nome: N_('Cliente'), desc: N_('Vê e envia os documentos da própria pasta e responde aos pedidos.'), segs: ['fin'], ver: ['*'], editar: ['documento', 'pedido', 'recado'] },
  },
  mudanca: {
    ajudante: { nome: N_('Ajudante'), desc: N_('Caixas e cronograma da mudança.'), segs: [], ver: ['*'], editar: ['caixa', 'dia'] },
  },
};
// papel de quem entra pelo convite, até o administrador escolher outro
// (abas sem papel aqui começam como "Participa": viagens, eventos, mudança, família, leitura, cuidado...)
const PADRAO: Partial<Record<ModId, string>> = {
  obras: 'mestre', consultorio: 'secretaria', hospedagem: 'limpeza', professor: 'aluno', portal: 'cliente',
};
/** Abas em que o grupo é colaborativo por padrão (quem entra participa de tudo). */
export const ehColaborativa = (mod: ModId) => !PADRAO[mod];

/** Papéis disponíveis na aba (os próprios dela primeiro) e o papel de quem acabou de entrar ("membro"). */
export function papeisDaAba(mod: ModId): Papeis {
  // Diário › Projetos em Grupo (0.65): só "Edita tudo" ou "Só acompanha"; quem entra pelo convite edita
  if (mod === 'diario') return { editor: EDITOR, leitor: LEITOR, membro: { ...EDITOR } };
  const p: Papeis = { participante: PARTICIPA, ...(ESPECIFICOS[mod] ?? {}), editor: EDITOR, leitor: LEITOR, leitor_sv: LEITOR_SV };
  // texto 13 G: no grupo de leitura, quem entra pelo convite acompanha (vê o material do livro e a leitura, sem alterar).
  // Quem administra pode passar a pessoa para "Participa" ou "Edita tudo". O servidor aplica o papel (só leitura).
  if (mod === 'leitura') return { ...p, membro: { ...LEITOR } };
  return { ...p, membro: { ...p[PADRAO[mod] ?? 'participante'] } };
}

/** Parte da ficha onde o campo fica: valores em "fin", dados clínicos em "clin", o resto na base. */
export const segDoCampo = (f: Campo): Seg => f.seg ?? (f.t === 'dinheiro' ? 'fin' : 'base');
export function segDaChave(col: Colecao | undefined, k: string): Seg {
  const f = col?.campos.find(x => x.k === k);
  if (f) return segDoCampo(f);
  if (k === 'moedas') return 'fin';   // a moeda de cada valor (0.69, B1) fica junto com os valores
  if (k.endsWith('__rico')) return segDaChave(col, k.slice(0, -'__rico'.length));   // 11f H: o conteúdo do editor fica junto com o campo dele
  return col?.segExtra?.[k] ?? 'base';
}
